import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Popover,
  PopoverContent,
  PopoverTrigger,
  VStack,
  HStack,
  Text,
  Select,
  useDisclosure,
} from '@chakra-ui/react';
import { Icon } from 'src/components/atoms/Icon';
import { Checkbox } from 'src/components/atoms/Checkbox';

type Campaign = {
  id: number;
  name: string;
  category: string;
};

type CampaignsMultiSelectProps = {
  campaigns: Campaign[];
  selectedIds: Set<number>;
  onSelectionChange: (selected: Set<number>) => void;
};

export const CampaignsMultiSelect: React.FC<CampaignsMultiSelectProps> = ({
  campaigns,
  selectedIds,
  onSelectionChange,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const triggerRef = useRef<HTMLDivElement>(null);
  const [triggerWidth, setTriggerWidth] = useState<number | undefined>(
    undefined,
  );

  // Get unique categories
  const categories = useMemo(() => {
    return [...new Set(campaigns.map((c) => c.category))];
  }, [campaigns]);

  // Filter campaigns by category and search
  const filteredCampaigns = useMemo(() => {
    let result = campaigns;
    if (selectedCategory) {
      result = result.filter((c) => c.category === selectedCategory);
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((c) => c.name.toLowerCase().includes(query));
    }
    return result;
  }, [campaigns, selectedCategory, searchQuery]);

  const handleToggle = (campaignId: number) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(campaignId)) {
      newSelected.delete(campaignId);
    } else {
      newSelected.add(campaignId);
    }
    onSelectionChange(newSelected);
  };

  useEffect(() => {
    if (triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, [isOpen]);

  const displayValue =
    selectedIds.size > 0 ? `${selectedIds.size} campaigns selected` : '';

  return (
    <FormControl>
      <FormLabel
        fontFamily="marketingPage"
        fontSize="16px"
        fontWeight={500}
        color="content.title"
        mb={3}>
        Campaigns
      </FormLabel>
      <Popover isOpen={isOpen} onClose={onClose} onOpen={onOpen} isLazy>
        <PopoverTrigger>
          <InputGroup ref={triggerRef}>
            <Input
              placeholder="Select campaigns"
              value={displayValue}
              readOnly
              cursor="pointer"
              onClick={onOpen}
              bg="surface.panel"
              border="1px solid"
              borderColor="surface.border"
              borderRadius="md"
              fontSize="16px"
              color="content.title"
              fontFamily="marketingPage"
              _placeholder={{ color: 'content.muted' }}
              _focus={{
                borderColor: 'icon.green',
                boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
              }}
            />
            <InputRightElement pointerEvents="none">
              <Icon
                iconName={isOpen ? 'chevron-up' : 'chevron-down'}
                boxSize={4}
                color="content.muted"
              />
            </InputRightElement>
          </InputGroup>
        </PopoverTrigger>
        <PopoverContent
          p={4}
          bg="surface.panel"
          borderRadius="md"
          boxShadow="lg"
          w={triggerWidth ? `${triggerWidth}px` : '100%'}>
          {/* Category Filter */}
          <Box mb={3}>
            <Select
              placeholder="All categories"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              size="sm"
              border="1px solid"
              borderColor="surface.border"
              borderRadius="md">
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </Select>
          </Box>

          {/* Search Input */}
          <Box mb={3}>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon iconName="search" boxSize={4} color="content.muted" />
              </InputLeftElement>
              <Input
                placeholder="Search campaigns"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                border="1px solid"
                borderColor="surface.border"
                borderRadius="md"
                pl={10}
                fontFamily="marketingPage"
                _placeholder={{ color: 'content.muted' }}
                _focus={{
                  borderColor: 'icon.green',
                  boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
                }}
              />
            </InputGroup>
          </Box>

          {/* Campaigns List */}
          <Box maxH="200px" overflowY="auto">
            <VStack align="stretch" spacing={1}>
              {filteredCampaigns.map((campaign) => (
                <HStack
                  key={campaign.id}
                  p={2}
                  borderRadius="md"
                  _hover={{ bg: 'sidebar.itemHoverBg' }}
                  cursor="pointer"
                  onClick={() => handleToggle(campaign.id)}>
                  <Checkbox
                    isChecked={selectedIds.has(campaign.id)}
                    pointerEvents="none"
                  />
                  <Box>
                    <Text fontSize="sm" fontFamily="marketingPage">
                      {campaign.name}
                    </Text>
                    <Text fontSize="xs" color="content.muted">
                      {campaign.category}
                    </Text>
                  </Box>
                </HStack>
              ))}
            </VStack>
          </Box>
        </PopoverContent>
      </Popover>
    </FormControl>
  );
};
